"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.KICAD_PIN_ELECTRICAL_TYPES = void 0;
exports.parseSchematic = parseSchematic;
exports.kicadAddNetDifferentialPairInfo = kicadAddNetDifferentialPairInfo;
exports.setLayerSelector = setLayerSelector;
exports.preParseSchematicSheet = preParseSchematicSheet;
exports.shouldStoreKicadSourceReference = shouldStoreKicadSourceReference;
exports.isAnnotatedKicadModuleName = isAnnotatedKicadModuleName;
exports.getShouldAnnotateModuleName = getShouldAnnotateModuleName;
exports.parseText = parseText;
exports.parseSheetPins = parseSheetPins;
exports.parsePolyline = parsePolyline;
exports.parseComponents = parseComponents;
exports.parseComponentDefinition = parseComponentDefinition;
exports.parseSymbol = parseSymbol;
exports.combineNets = combineNets;
const Area_1 = require("../../Area");
const layers_1 = require("../layers");
const constants_1 = require("../constants");
const Util_1 = require("../../Util");
const project_1 = require("../project");
const v6ParseArc_1 = require("../common/v6ParseArc");
const v6ParseImage_1 = require("../common/v6ParseImage");
const v6ParseRectangle_1 = require("../common/v6ParseRectangle");
const v6ParseLabel_1 = require("../common/v6ParseLabel");
const v6ExtractPolygon_1 = require("../common/v6ExtractPolygon");
const v6ParseTitleBlock_1 = require("../common/v6ParseTitleBlock");
const v6ParseCircle_1 = require("../common/v6ParseCircle");
const v6Common_1 = require("../common/v6Common");
const v6ExtractSheetInfo_1 = require("../common/v6ExtractSheetInfo");
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const RefableNet_1 = require("../RefableNet");
const DesignModels_1 = require("@cadstrom/sigma-engine-shared/dist/DesignModels");
const Utils_1 = require("@cadstrom/sigma-engine-shared/dist/Utils");
const Transform_1 = require("@cadstrom/sigma-engine-shared/dist/Utils/Transform");
const fonts_1 = require("../../Fonts/fonts");
const schematicUtils_1 = require("../../schematicUtils");
const sigma_engine_shared_2 = require("@cadstrom/sigma-engine-shared");
const Constants_1 = require("../../Constants");
const nodeIsHidden_1 = require("../common/nodeIsHidden");
const parseTextTransforms_1 = require("./parseTextTransforms");
const getMirrorValuesToKeepTextRightSideUp_1 = require("./getMirrorValuesToKeepTextRightSideUp");
const kicadSymbolParser_1 = require("./kicadSymbolParser");
const KicadNetCombobulator_1 = require("./KicadNetCombobulator");
const KicadNetNamingStrategy_1 = require("./KicadNetNamingStrategy");
const KicadHierarchyResolver_1 = require("../KicadHierarchyResolver");
const KicadBusTopology_1 = require("./KicadBusTopology");
const DEFAULT_LINE_WIDTH = 0.1;
let moduleNames;
let layerSelector;
function parseSchematic(def, parser, project, sch, resolver, netRefCtx, netCombobulationOpts, extractedImages) {
    if (project.type !== 'kicad') {
        throw new Error('Cannot parse kicad schematic to non-kicad project');
    }
    moduleNames = new Set();
    layerSelector = new layers_1.KicadSchLayerSelector();
    const parsedPage = typeof sch.page === 'string' ? parseInt(sch.page, 10) : sch.page;
    const sheetIndex = typeof parsedPage === 'number' && Number.isFinite(parsedPage) ? parsedPage : sch.numberingOrder;
    const model = {
        modelType: 'schematic',
        designId: sch.uuid,
        designName: sch.name,
        sourceFile: sch.filename,
        grid: v6Common_1.DefaultSchGrid,
        sheetIndex: (0, Utils_1.max)(sheetIndex, 0),
        edges_bbox: {
            minx: Number.POSITIVE_INFINITY,
            miny: Number.POSITIVE_INFINITY,
            maxx: Number.NEGATIVE_INFINITY,
            maxy: Number.NEGATIVE_INFINITY,
        },
        initial_zoombox: {
            minx: Number.POSITIVE_INFINITY,
            miny: Number.POSITIVE_INFINITY,
            maxx: Number.NEGATIVE_INFINITY,
            maxy: Number.NEGATIVE_INFINITY,
        },
        font: {
            default: fonts_1.Font.Newstroke,
        },
        layerSet: layers_1.KICAD_SCH_LAYERSET,
        drawings: {},
        modules: [],
        ports: {},
        nets: (0, RefableNet_1.refableObject)({}, netRefCtx, (r, n1, n2) => combineNets(r, n1, n2)),
        warnings: [],
        metadata: {},
    };
    const warnings = model.warnings;
    if (!project.layerSets[layers_1.KICAD_SCH_LAYERSET]) {
        project.layerSets[layers_1.KICAD_SCH_LAYERSET] = (0, layers_1.getKicadSchLayers)().map((layer) => {
            layer.defaultVisible = typeof layer.defaultVisible !== 'boolean' || layer.defaultVisible === true;
            return layer;
        });
    }
    const tree = parser.parseFile(sch.filename, sch.contents);
    const globalDriverNames = new Set();
    const netCalc = new KicadNetCombobulator_1.KicadNetCombobulator(netRefCtx, netCombobulationOpts, new KicadNetNamingStrategy_1.KicadNetNamingStrategy(sch.netPath, globalDriverNames));
    const busTopology = new KicadBusTopology_1.KicadBusTopology();
    const busAliases = new Map();
    const wires = [];
    const localLabels = [];
    const symbolNodes = [];
    const sheetNodes = [];
    const hierarchicalLabelNodes = [];
    tree.nodes.forEach((node) => {
        switch (node.name) {
            case 'lib_symbols': {
                parseComponents(project, node, netRefCtx, resolver);
                break;
            }
            case 'junction': {
                const junction = parseJunction(node);
                busTopology.addJunction(junction.position);
                (0, schematicUtils_1.addSchematicDrawing)(model, (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Junction).id, junction);
                break;
            }
            case 'no_connect': {
                const position = parseNoConnectPosition(node);
                if (position) {
                    netCalc.namingStrategy.addNoConnectMarker(position, netCalc.pointRoundingPrecision / 2);
                }
                break;
            }
            case 'wire': {
                const wire = parseWire(node);
                wires.push(wire);
                netCalc.addWire({
                    from: wire.start,
                    to: wire.end,
                });
                (0, schematicUtils_1.addSchematicDrawing)(model, (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Wire).id, wire);
                break;
            }
            case 'bus': {
                const bus = parseWire(node);
                busTopology.addTrack(bus);
                (0, schematicUtils_1.addSchematicDrawing)(model, (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Bus).id, bus);
                break;
            }
            case 'bus_entry': {
                const wire = parseBusEntry(node);
                wires.push(wire);
                netCalc.addWire({
                    from: wire.start,
                    to: wire.end,
                });
                (0, schematicUtils_1.addSchematicDrawing)(model, (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Wire).id, wire);
                break;
            }
            case 'image': {
                const image = (0, v6ParseImage_1.parseImage)(node, warnings);
                if (image) {
                    (0, schematicUtils_1.addSchematicDrawing)(model, (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Image).id, image.drawing);
                    extractedImages?.push(image.asset);
                }
                break;
            }
            case 'polyline': {
                const segments = parsePolyline(node, false);
                for (const segment of segments) {
                    (0, schematicUtils_1.addSchematicDrawing)(model, (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Other).id, segment);
                }
                break;
            }
            case 'global_label': {
                const label = parseGlobalLabel(node, netRefCtx, warnings);
                const labelValue = KicadNetNamingStrategy_1.KicadNetNamingStrategy.escapeNetName(label.value.valueOf());
                globalDriverNames.add(labelValue);
                const netRef = netCalc.setLabel(label.pos, labelValue, 'global');
                (0, schematicUtils_1.addSchematicDrawing)(model, (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Label).id, label);
                label.net = netRef.strCast();
                break;
            }
            case 'text': {
                const text = (0, v6ParseLabel_1.parseTextElement)(node, false, warnings, undefined, layerSelector);
                (0, schematicUtils_1.addSchematicDrawing)(model, text.layers[0], text.drawing);
                break;
            }
            case 'text_box': {
                const textBox = (0, v6ParseLabel_1.parseTextBox)(node, false, warnings, (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Other).name, layerSelector);
                (0, schematicUtils_1.addSchematicDrawing)(model, textBox.layers[0], textBox.drawing);
                break;
            }
            case 'label': {
                {
                    const label = (0, v6ParseLabel_1.parseLabel)(model, node, false, warnings, netRefCtx, (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Label).name, layerSelector, resolver, true);
                    if (label.drawing.value.valueOf() !== '') {
                        if (KicadBusTopology_1.KicadBusTopology.isBusLabel(label.drawing.value.valueOf())) {
                            busTopology.addDriver(label.drawing.value.valueOf(), KicadBusTopology_1.KicadBusDriverType.LocalLabel, label.drawing.pos);
                        }
                        else {
                            localLabels.push(label.drawing);
                        }
                    }
                    (0, schematicUtils_1.addSchematicDrawing)(model, label.layers[0], label.drawing);
                    break;
                }
            }
            case 'hierarchical_label': {
                hierarchicalLabelNodes.push(node);
                break;
            }
            case 'bus_alias': {
                const aliasName = (0, Util_1.removeQuotes)(node.values[0] ?? '');
                const membersNode = node.nodes.find((child) => child.name === 'members');
                if (aliasName.length > 0 && membersNode) {
                    busAliases.set(aliasName, membersNode.values.map((member) => (0, Util_1.removeQuotes)(member)));
                }
                break;
            }
            case 'uuid': {
                const value = (0, Util_1.cleanMetadataVal)(node.values[0]);
                if (value) {
                    model.metadata['kicad.uuid'] = value;
                }
                break;
            }
            case 'sheet': {
                sheetNodes.push(node);
                break;
            }
            case 'symbol': {
                symbolNodes.push(node);
                break;
            }
            case 'title_block': {
                (0, v6ParseTitleBlock_1.parseTitleBlock)(model, node);
                break;
            }
            case 'rectangle': {
                const drawings = (0, v6ParseRectangle_1.parseRectangle)(node, false, warnings, DesignModels_1.SchLayerType.Other, layerSelector, Constants_1.SEGMENT_MIN_WIDTH);
                for (const drawing of drawings) {
                    for (const layer of drawing.layers) {
                        (0, schematicUtils_1.addSchematicDrawing)(model, layer, drawing.drawing);
                    }
                }
                break;
            }
            case 'circle': {
                const obj = (0, v6ParseCircle_1.readCircle)(node, layerSelector.getLayerByType(DesignModels_1.SchLayerType.Other).name);
                const layers = layerSelector.filterLayers(obj.layer);
                (0, schematicUtils_1.addSchematicDrawing)(model, layers[0].id, obj.circle);
                break;
            }
            case 'arc': {
                const drawing = (0, v6ParseArc_1.parseArc)(false, node, false, warnings, layerSelector.getLayerByType(DesignModels_1.SchLayerType.Other).name, layerSelector);
                if (drawing) {
                    for (const layer of drawing.layers) {
                        (0, schematicUtils_1.addSchematicDrawing)(model, layer, drawing.drawing);
                    }
                }
                break;
            }
        }
    });
    symbolNodes.forEach((node) => {
        switch (node.name) {
            case 'symbol': {
                const m = parseModule(project, node, warnings, model, netCalc, resolver, {
                    globalDriverNames,
                    instancePath: sch.instancePath,
                    annotationPath: sch.annotationPath,
                    projectName: sch.projectName,
                    legacySymbolInstances: sch.legacySymbolInstances,
                    sourceInstanceCount: sch.sourceInstanceCount,
                });
                resolver.addComponentPart(m);
                model.modules.push(m);
                break;
            }
        }
    });
    for (const node of hierarchicalLabelNodes) {
        const rawValue = (0, Util_1.removeQuotes)(node.values[0] ?? '');
        const label = parseHierarchicalLabel(model, node, netCalc, warnings, resolver);
        if (KicadBusTopology_1.KicadBusTopology.isBusLabel(rawValue)) {
            busTopology.addDriver(rawValue, KicadBusTopology_1.KicadBusDriverType.HierarchicalLabel, label.pos);
        }
        (0, schematicUtils_1.addSchematicDrawing)(model, (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Label).id, label);
    }
    for (const label of localLabels) {
        const labelValue = KicadNetNamingStrategy_1.KicadNetNamingStrategy.escapeNetName(label.value.valueOf());
        const netRef = netCalc.setLabel(label.pos, globalDriverNames.has(labelValue) ? labelValue : sch.netPath + labelValue, 'local_label');
        label.net = netRef.strCast();
    }
    sheetNodes.forEach((node, sheetSymbolIndex) => {
        switch (node.name) {
            case 'sheet': {
                const sheet = parseSheet(def, project, model, sch, node, sheetSymbolIndex + 1, warnings, netCalc, resolver);
                const component = project.components[sheet.drawModule.componentRef];
                const symbol = component?.symbols.find((candidate) => candidate.ref === sheet.drawModule.footprintRef);
                const pinNodes = node.nodes.filter((child) => child.name === 'pin');
                for (let index = 0; index < pinNodes.length; index++) {
                    const pinNode = pinNodes[index];
                    const pin = symbol?.pins[index];
                    const pinName = (0, Util_1.removeQuotes)(pinNode.values[0] ?? '');
                    const atNode = pinNode.nodes.find((child) => child.name === 'at');
                    if (!pin || !atNode || !KicadBusTopology_1.KicadBusTopology.isBusLabel(pinName)) {
                        continue;
                    }
                    busTopology.addDriver(pinName, KicadBusTopology_1.KicadBusDriverType.SheetPin, [parseFloat(atNode.values[0]), parseFloat(atNode.values[1])], sheet.drawModule.ref, pin.ref);
                }
                (0, schematicUtils_1.addSchematicDrawing)(model, (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Symbol).id, sheet);
                break;
            }
        }
    });
    (0, KicadHierarchyResolver_1.recordKicadBusAliases)(project, busAliases, sch.netPath);
    (0, KicadHierarchyResolver_1.recordKicadBusSheet)(model, busTopology.build());
    const nets = netCalc.computeNets();
    const resolvedPinHintNames = netCalc.namingStrategy.resolvePinHintNetNames(nets.values());
    const netNames = new Set(Array.from(nets.keys(), (name) => resolvedPinHintNames.get(name) ?? name));
    for (const net of nets.values()) {
        const computedNetName = String(net.name.strCast());
        const baseNetName = resolvedPinHintNames.get(computedNetName) ?? computedNetName;
        let netName = baseNetName;
        let suffix = 1;
        while (model.nets[netName]) {
            netName = `${baseNetName}_${suffix++}`;
        }
        for (const netRef of net.netRefs) {
            netRef.setName(netName);
        }
        for (const connection of net.connections) {
            const driver = connection.connection.appliesNetName;
            if (driver && (driver.labelType === 'pin_hint' || driver.label === baseNetName)) {
                driver.label = netName;
            }
        }
        const isGlobal = net.labels.some((l) => l.labelType === 'global' || l.labelType === 'power_pin');
        const isHierarchical = net.labels.some((l) => l.labelType === 'hier_label');
        const netInfo = {
            name: net.name.strCast(),
            connections: [],
            isGlobal,
            isHierarchical,
            netLabels: net.labels.map((l) => ({
                type: l.labelType,
                position: l.position,
                label: l.originalLabel,
                isGlobal: l.labelType === 'global' || l.labelType === 'power_pin',
                isHierarchical: l.labelType === 'hier_label',
            })),
        };
        (0, KicadHierarchyResolver_1.recordKicadNetDrivers)(netInfo, net, sch.netPath);
        model.nets[netName] = netInfo;
        for (const connection of net.connections) {
            const module = model.modules.find((m) => m.ref === connection.connection.moduleRef);
            if (!module) {
                const schRef = model.drawings[(0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Symbol).id]?.find((d) => d.type === 'schref' && d.drawModule.ref === connection.connection.moduleRef);
                if (schRef) {
                    warnings.push(`Net connections to schematic refs not yet supported: ${net.name.toString()} => ${schRef.name}::${connection.connection.pinRef}`);
                    continue;
                }
                throw new Error('Internal consistency error in parsing net module connections');
            }
            module.pinNets[connection.connection.pinRef] = netName;
            if (!model.nets[netName].connections.find((c) => c.moduleRef === connection.connection.moduleRef && c.pinRef === connection.connection.pinRef)) {
                model.nets[netName].connections.push(connection.connection);
            }
        }
        kicadAddNetDifferentialPairInfo(netInfo.name, netInfo, netNames, netRefCtx, warnings);
    }
    wires.forEach((wire) => {
        const net = netCalc.getNetAtPosition(wire.start) || netCalc.getNetAtPosition(wire.end);
        if (net && net.netRefs.size > 0) {
            const netRef = Array.from(net.netRefs)[0];
            wire.net = netRef.strCast();
        }
    });
    const bbox = new Area_1.default();
    bbox.extendFromSchModel(project, model, project.layerSets[layers_1.KICAD_SCH_LAYERSET], warnings);
    model.edges_bbox = {
        minx: bbox.xMin,
        miny: bbox.yMin,
        maxx: bbox.xMax,
        maxy: bbox.yMax,
    };
    const visibleBbox = new Area_1.default();
    visibleBbox.extendFromSchModel(project, model, project.layerSets[layers_1.KICAD_SCH_LAYERSET], model.warnings, false);
    model.initial_zoombox = {
        minx: visibleBbox.xMin,
        miny: visibleBbox.yMin,
        maxx: visibleBbox.xMax,
        maxy: visibleBbox.yMax,
    };
    model.warnings = warnings;
    (0, Util_1.dedupeWarnings)(model);
    moduleNames = undefined;
    layerSelector = undefined;
    return model;
}
function kicadAddNetDifferentialPairInfo(netName, netInfo, netNames, netRefCtx, warnings) {
    if (netInfo) {
        netInfo.name = netName;
    }
    const { diffPairName, currentIsPositive, diffPairComplementName } = getDiffPairName(netName);
    if (diffPairName) {
        netInfo.diffPair = {
            pairName: diffPairName,
            positiveId: netRefCtx.existingOrNewRef(currentIsPositive ? netName : diffPairComplementName).strCast(),
            negativeId: netRefCtx.existingOrNewRef(currentIsPositive ? diffPairComplementName : netName).strCast(),
        };
        if ((0, sigma_engine_shared_1.isNetRef)(netInfo.name)) {
            netInfo.name.on('change', (newVal) => {
                const { diffPairName, currentIsPositive, diffPairComplementName } = getDiffPairName(newVal.strCast());
                netInfo.diffPair.pairName = diffPairName;
                netInfo.diffPair.positiveId = currentIsPositive ? newVal.strCast() : diffPairComplementName;
                netInfo.diffPair.negativeId = currentIsPositive ? diffPairComplementName : newVal.strCast();
            });
        }
        if (!netNames.has(netInfo.diffPair.positiveId.toString())) {
            const lowerSearch = netInfo.diffPair.positiveId.toString().toLowerCase();
            let found = false;
            for (const net of netNames) {
                if (net.toLowerCase() === lowerSearch) {
                    netInfo.diffPair.positiveId = net;
                    found = true;
                    break;
                }
            }
            if (!found) {
                warnings.push(`Could not find positive net for differential pair '${netInfo.diffPair.pairName}'`);
            }
        }
        if (!netNames.has(netInfo.diffPair.negativeId.toString())) {
            const lowerSearch = netInfo.diffPair.negativeId.toString().toLowerCase();
            let found = false;
            for (const net of netNames) {
                if (net.toLowerCase() === lowerSearch) {
                    netInfo.diffPair.negativeId = net;
                    found = true;
                    break;
                }
            }
            if (!found) {
                warnings.push(`Could not find negative net for differential pair '${netInfo.diffPair.pairName}'`);
            }
        }
    }
}
function getDiffPairName(netName) {
    let diffPairName;
    let currentIsPositive = false;
    let diffPairComplementName;
    if (netName?.toLowerCase().endsWith('_p') || netName?.toLowerCase().endsWith('_n')) {
        diffPairName = netName.slice(0, -2);
        currentIsPositive = netName.toLowerCase().endsWith('_p');
        if (currentIsPositive) {
            diffPairComplementName = diffPairName + '_N';
        }
        else {
            diffPairComplementName = diffPairName + '_P';
        }
    }
    else if (netName.endsWith('+') || netName.endsWith('-')) {
        diffPairName = netName.slice(0, -1);
        currentIsPositive = netName.endsWith('+');
        if (currentIsPositive) {
            diffPairComplementName = diffPairName + '-';
        }
        else {
            diffPairComplementName = diffPairName + '+';
        }
    }
    return { diffPairName, currentIsPositive, diffPairComplementName };
}
function setLayerSelector(selector) {
    layerSelector = selector;
}
function preParseSchematicSheet(schId, schContents, parser, warnings, projectName) {
    const mainSheet = {
        numberingOrder: Number.NEGATIVE_INFINITY,
        uuid: schId,
        name: schId,
        filename: schId,
        contents: schContents,
        netPath: '/',
        projectName,
    };
    const sheets = [mainSheet];
    const tree = parser.parseFile(schId, schContents);
    let maxX = 1;
    let minX = 0;
    let sheetSymbolIndex = 0;
    const sheetUuidOccurrences = new Map();
    for (const node of tree.nodes) {
        switch (node.name) {
            case 'uuid': {
                if (typeof node.values[0] === 'string') {
                    mainSheet.uuid = (0, Util_1.removeQuotes)(node.values[0]);
                }
                break;
            }
            case 'sheet': {
                sheetSymbolIndex += 1;
                const sheetInfo = (0, v6ExtractSheetInfo_1.extractSheetInfo)(node);
                if (!sheetInfo.uuid || !sheetInfo.sheetfile || !sheetInfo.sheetname) {
                    continue;
                }
                const sourceSymbolOccurrence = (sheetUuidOccurrences.get(sheetInfo.uuid) ?? 0) + 1;
                sheetUuidOccurrences.set(sheetInfo.uuid, sourceSymbolOccurrence);
                let instances;
                for (const n2 of node.nodes) {
                    if (n2.name === 'instances') {
                        instances = extractSymbolInstances(n2, warnings, projectName);
                    }
                }
                maxX = Math.max(maxX, sheetInfo.start[0] + sheetInfo.size[0]);
                minX = Math.min(minX, sheetInfo.start[0]);
                const parentInstances = instances?.instances.length ? instances.instances : [undefined];
                const altiumRepeatMatch = sheetInfo.sheetname.match(/Repeat\(([^,]+),(\d+),(\d+)\)/i);
                if (altiumRepeatMatch) {
                    const channelIdentifier = altiumRepeatMatch[1];
                    const channelIndex1 = parseInt(altiumRepeatMatch[2]);
                    const lastChannelIndexN = parseInt(altiumRepeatMatch[3]);
                    if (typeof channelIndex1 !== 'number' ||
                        typeof lastChannelIndexN !== 'number' ||
                        Number.isNaN(channelIndex1) ||
                        Number.isNaN(lastChannelIndexN)) {
                        warnings.push(`Invalid channel index in sheet name ${sheetInfo.sheetname} - ${altiumRepeatMatch[2]} or ${altiumRepeatMatch[3]} is not a number`);
                        continue;
                    }
                    if (channelIndex1 < 0 || lastChannelIndexN < 0) {
                        warnings.push(`Invalid channel index in sheet name ${sheetInfo.sheetname} - ${altiumRepeatMatch[2]} or ${altiumRepeatMatch[3]} is negative`);
                        continue;
                    }
                    if (channelIndex1 > lastChannelIndexN) {
                        warnings.push(`Invalid channel index in sheet name ${sheetInfo.sheetname} - ${altiumRepeatMatch[2]} is greater than ${altiumRepeatMatch[3]}`);
                        continue;
                    }
                    for (const parentInstance of parentInstances) {
                        for (let i = channelIndex1; i <= lastChannelIndexN; ++i) {
                            const childUuid = i === channelIndex1 ? sheetInfo.uuid : `${sheetInfo.uuid}-${i}`;
                            const parentInstancePath = parentInstance ? (0, project_1.joinKicadInstancePath)(parentInstance.path, '') : undefined;
                            const annotationPath = parentInstancePath ? (0, project_1.joinKicadInstancePath)(parentInstancePath, childUuid) : undefined;
                            sheets.push({
                                numberingOrder: i,
                                uuid: childUuid,
                                filename: simplePathResolve(sheetInfo.sheetfile, mainSheet.filename),
                                name: `${channelIdentifier}${i}`,
                                parentInstancePath,
                                instancePath: annotationPath,
                                annotationPath,
                                instanceProjectName: parentInstance?.projectName,
                                sourceSymbolKey: `${sheetSymbolIndex}:${childUuid}`,
                                sourceSymbolOccurrence,
                                sourceSheetUuid: childUuid,
                                page: parentInstance?.page,
                                netPath: '/',
                                render: {
                                    start: sheetInfo.start,
                                    size: sheetInfo.size,
                                },
                                metadata: {
                                    'altium-RoomName': `${channelIdentifier}${i}`,
                                    'altium-ChannelPrefix': channelIdentifier,
                                    'altium-ChannelIndex': i,
                                },
                            });
                        }
                    }
                }
                else {
                    for (const parentInstance of parentInstances) {
                        const parentInstancePath = parentInstance ? (0, project_1.joinKicadInstancePath)(parentInstance.path, '') : undefined;
                        const annotationPath = parentInstancePath ? (0, project_1.joinKicadInstancePath)(parentInstancePath, sheetInfo.uuid) : undefined;
                        sheets.push({
                            numberingOrder: 0,
                            uuid: sheetInfo.uuid,
                            filename: simplePathResolve(sheetInfo.sheetfile, mainSheet.filename),
                            name: sheetInfo.sheetname,
                            parentInstancePath,
                            instancePath: annotationPath,
                            annotationPath,
                            instanceProjectName: parentInstance?.projectName,
                            sourceSymbolKey: `${sheetSymbolIndex}:${sheetInfo.uuid}`,
                            sourceSymbolOccurrence,
                            sourceSheetUuid: sheetInfo.uuid,
                            page: parentInstance?.page,
                            netPath: '/',
                            render: {
                                start: sheetInfo.start,
                                size: sheetInfo.size,
                            },
                        });
                    }
                }
                break;
            }
            case 'sheet_instances': {
                mainSheet.legacySheetInstances = extractLegacySheetInstances(node);
                mainSheet.page = mainSheet.legacySheetInstances.find((instance) => instance.path === '/')?.page;
                break;
            }
            case 'symbol_instances': {
                mainSheet.legacySymbolInstances = extractLegacySymbolInstances(node);
                break;
            }
        }
    }
    for (const sheet of sheets) {
        if (!sheet.render) {
            continue;
        }
        sheet.numberingOrder =
            (sheet.render.start[0] + sheet.render.start[1] * (maxX - minX + 1)) * 1000 + sheet.numberingOrder;
    }
    sheets.sort((a, b) => a.numberingOrder - b.numberingOrder);
    for (let i = 0; i < sheets.length; ++i) {
        sheets[i].numberingOrder = i + 1;
    }
    return sheets;
}
function simplePathResolve(path, base) {
    if (path.startsWith('/')) {
        return path;
    }
    const parts = base.split('/');
    parts.pop();
    parts.push(path);
    for (let i = 0; i < parts.length; ++i) {
        if (parts[i] === '..' && i > 0) {
            parts.splice(i - 1, 2);
            i -= 2;
        }
    }
    return parts.join('/');
}
function getModuleUnitNumberSuffix(unitNo) {
    return (0, KicadNetNamingStrategy_1.kicadUnitSubReference)(unitNo);
}
function shouldStoreKicadSourceReference(libraryUnitCount, isMultiUnit, moduleName, sourceReference) {
    return (libraryUnitCount > 1 &&
        !isMultiUnit &&
        sourceReference.length > 0 &&
        sourceReference !== moduleName &&
        !sourceReference.endsWith('?') &&
        !sourceReference.startsWith('#'));
}
function isAnnotatedKicadModuleName(moduleName) {
    return moduleName.startsWith('#');
}
function getShouldAnnotateModuleName(moduleName) {
    if (!moduleName.startsWith('#')) {
        return false;
    }
    if (moduleName.endsWith('?')) {
        return true;
    }
    return !moduleName.match(/\d+$/);
}
function selectPlacedUnitSymbol(symbols, unitNo, bodyStyle, unitStr) {
    const byBodyStyleThenName = (left, right) => {
        const bodyStyleOrder = (left.metadata?.bodyStyle ?? 0) - (right.metadata?.bodyStyle ?? 0);
        return bodyStyleOrder !== 0 ? bodyStyleOrder : left.name.localeCompare(right.name);
    };
    const placedUnitSymbols = typeof unitNo === 'number' ?
        symbols.filter((symbol) => symbol.metadata?.unit === unitNo).sort(byBodyStyleThenName)
        : [];
    const unitZeroSymbols = typeof unitNo === 'number' ?
        symbols.filter((symbol) => symbol.metadata?.unit === 0 && symbol.pins.length > 0).sort(byBodyStyleThenName)
        : [];
    return (placedUnitSymbols.find((symbol) => symbol.metadata?.bodyStyle === bodyStyle) ??
        placedUnitSymbols.find((symbol) => symbol.pins.length > 0) ??
        unitZeroSymbols.find((symbol) => symbol.metadata?.bodyStyle === 1) ??
        unitZeroSymbols[0] ??
        placedUnitSymbols[0] ??
        symbols.find((symbol) => symbol.name.endsWith(unitStr)));
}
function getDefaultPinName(pin) {
    return pin.name === '~' || pin.name === '' || pin.name === pin.number ? pin.number : pin.name;
}
function parseModule(project, node, warnings, model, netCalc, resolver, opts = {}) {
    let componentName = opts.defaultComponentName || '';
    let componentNameOverride;
    let moduleName = opts.moduleName || '';
    let sourceReference = opts.moduleName || '';
    let moduleRef = opts.moduleRef || '';
    let pos = [0, 0];
    let mirrored = [false, false];
    let angle = 0;
    let inBom = !opts.hideInBom;
    let dnp = Boolean(opts.isDnp);
    let unitNo;
    let bodyStyle = 1;
    let unitStr = '';
    let instances;
    const drawProperties = { ...opts.drawProperties };
    let value;
    const metadata = {};
    node.nodes.forEach((n) => {
        switch (n.name) {
            case 'lib_id': {
                componentName = (0, Util_1.removeQuotes)(n.values[0]);
                break;
            }
            case 'lib_name': {
                componentNameOverride = (0, Util_1.removeQuotes)(n.values[0]);
                break;
            }
            case 'at': {
                pos = [n.values[0], n.values[1]].map((v) => parseFloat(v));
                if (typeof n.values[2] !== 'undefined') {
                    angle = (0, Transform_1.clampRotation)(parseFloat(n.values[2]));
                }
                break;
            }
            case 'pin': {
                break;
            }
            case 'in_bom': {
                inBom = n.values[0] === 'yes';
                break;
            }
            case 'dnp': {
                dnp = n.values[0] === 'yes';
                break;
            }
            case 'mirror': {
                switch (n.values[0]) {
                    case 'y':
                        mirrored = [false, true];
                        break;
                    case 'x':
                        mirrored = [true, false];
                        break;
                    default:
                        warnings.push(`Mirror value of ${n.values[0]} is not implemented.`);
                        break;
                }
                break;
            }
            case 'uuid': {
                if (n.values.length > 0) {
                    moduleRef = (0, Util_1.removeQuotes)(n.values[0]);
                }
                break;
            }
            case 'unit': {
                if (n.values.length === 1) {
                    const parsed = parseInt(n.values[0]);
                    if (Number.isFinite(parsed) && parsed > 0) {
                        unitNo = parsed;
                    }
                    else {
                        warnings.push(`Invalid module unit number: ${parsed}`);
                    }
                }
                break;
            }
            case 'convert':
            case 'body_style': {
                if (n.values.length === 1) {
                    const parsed = parseInt(n.values[0]);
                    if (Number.isFinite(parsed) && parsed >= 0) {
                        bodyStyle = parsed;
                    }
                    else {
                        warnings.push(`Invalid module body style number: ${parsed}`);
                    }
                }
                break;
            }
            case 'instances': {
                instances = extractSymbolInstances(n, project.warnings, opts.projectName);
                break;
            }
        }
    });
    const annotationPath = opts.annotationPath ?? opts.instancePath;
    const instanceInfo = annotationPath ?
        instances?.instances.find((instance) => instance.path === annotationPath)
        : instances?.instances.find((instance) => instance.finalNode === model.designId);
    const legacyInstanceInfo = legacySymbolInstanceForPath(opts.legacySymbolInstances, annotationPath, moduleRef);
    const resolvedInstanceInfo = instanceInfo ?? legacyInstanceInfo;
    const missingRepeatedLegacyAnnotation = Boolean(opts.sourceInstanceCount &&
        opts.sourceInstanceCount > 1 &&
        !opts.isSheetSymbol &&
        !instances &&
        !instanceInfo &&
        !legacyInstanceInfo);
    if (missingRepeatedLegacyAnnotation) {
        warnings.push(`KiCad 6 annotation missing for reused sheet ${model.sourceFile} at ${opts.instancePath ?? '/'}`);
    }
    if (typeof resolvedInstanceInfo?.unit === 'number') {
        unitNo = resolvedInstanceInfo.unit;
    }
    let useComponentNameOverride = componentNameOverride ?? componentName;
    if (resolver.removeAltiumSuffix) {
        useComponentNameOverride = resolver.removeAltiumSuffix(useComponentNameOverride, project);
    }
    const component = project.components[useComponentNameOverride];
    const hasExplicitUnitSymbols = component?.symbols.some((componentSymbol) => typeof componentSymbol.metadata?.unit === 'number');
    if (hasExplicitUnitSymbols) {
        unitNo ??= 1;
    }
    if (typeof unitNo === 'number') {
        unitStr = `_${unitNo}_${bodyStyle}`;
    }
    const symbolName = `${useComponentNameOverride}${unitStr}`;
    let moduleValue;
    let setDrawingValueToValue = false;
    node.nodes.forEach((n) => {
        switch (n.name) {
            case 'property': {
                const text = parseText(n);
                const propMetaValue = (0, Util_1.cleanMetadataVal)(text.drawing.value);
                if (text.drawing.ref && value) {
                    metadata[text.drawing.ref] = propMetaValue;
                }
                let layer;
                if (text.drawing.ref === 'Reference') {
                    const foundRef = resolvedInstanceInfo?.reference;
                    if (foundRef) {
                        text.drawing.value = foundRef;
                    }
                    else if (missingRepeatedLegacyAnnotation) {
                        text.drawing.value = kicadReferencePlaceholder(text.drawing.value);
                    }
                    sourceReference = text.drawing.value;
                    if (moduleNames?.has(text.drawing.value) && typeof unitNo === 'number') {
                        const updateModule = model.modules.find((m) => m.fullModuleName === text.drawing.value);
                        text.drawing.value += getModuleUnitNumberSuffix(unitNo);
                        if (updateModule &&
                            updateModule.metadata &&
                            updateModule.name === updateModule.fullModuleName &&
                            typeof updateModule.metadata[constants_1.META_KICAD_UNIT] === 'number') {
                            updateModule.name += getModuleUnitNumberSuffix(updateModule.metadata[constants_1.META_KICAD_UNIT]);
                        }
                        const updateText = updateModule?.drawProperties[text.drawing.ref];
                        if (updateText) {
                            updateText.value = updateModule.name;
                        }
                    }
                    moduleName = text.drawing.value;
                    layer = (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.NameLabel).id;
                    if (moduleName.startsWith('#PWR')) {
                        setDrawingValueToValue = true;
                    }
                }
                else if (text.drawing.ref === 'Value') {
                    layer = (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.ValueLabel).id;
                    value = text.drawing.value;
                    moduleValue = value;
                }
                else {
                    layer = (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Label).id;
                    const useValue = (0, Util_1.cleanMetadataVal)(text.drawing.value.trim());
                    if (text.drawing.ref && useValue && useValue !== '~') {
                        metadata[text.drawing.ref] = useValue;
                    }
                }
                if (angle === 90 || angle === 180) {
                    text.drawing.justify[0] *= -1;
                }
                const drawAngle = (0, Transform_1.roundToZero)((0, Transform_1.clampRotation)((text.drawing.angle || 0) - angle), 2);
                drawProperties[text.drawing.ref] = {
                    value: text.drawing.value,
                    angle: drawAngle,
                    pos: text.drawing.pos,
                    justify: text.drawing.justify,
                    hidden: text.hidden,
                    layer,
                };
                drawProperties[text.drawing.ref].mirror = (0, getMirrorValuesToKeepTextRightSideUp_1.getMirrorValuesToKeepTextRightSideUp)(drawAngle);
                break;
            }
        }
    });
    const concreteModulePath = opts.instancePath ?
        (0, project_1.kicadInstancePathWithoutRoot)((0, project_1.joinKicadInstancePath)(opts.instancePath, moduleRef))
        : (0, project_1.joinKicadInstancePath)('', moduleRef);
    const m = {
        name: moduleName,
        ref: resolver.validateSourceModuleRef(model.designId, concreteModulePath),
        footprintRef: symbolName,
        componentRef: component?.name || useComponentNameOverride,
        layer: 'F',
        center: pos,
        transform: {
            angle,
            mirrored,
        },
        drawProperties,
        pinNets: {},
    };
    if (unitNo !== undefined && component?.isMultiUnit) {
        if (isAnnotatedKicadModuleName(m.name)) {
            project.warnings.push(`Module ${m.name} is an annotated name, but is also a multi-unit component. This may not work correctly.`);
        }
        const unitSuffix = (0, KicadNetNamingStrategy_1.kicadUnitSubReference)(unitNo);
        if (m.name.endsWith(unitSuffix)) {
            m.fullModuleName = m.name.substring(0, m.name.length - unitSuffix.length);
        }
        else {
            m.fullModuleName = m.name;
            m.name = m.name + unitSuffix;
        }
    }
    const bareReference = sourceReference;
    const libraryUnitCount = Math.max(1, ...(component?.symbols ?? []).map((symbol) => typeof symbol.metadata?.unit === 'number' && symbol.metadata.unit > 0 ? symbol.metadata.unit : 0));
    const pinUnitReference = libraryUnitCount > 1 && unitNo ? `${bareReference}${(0, KicadNetNamingStrategy_1.kicadUnitSubReference)(unitNo)}` : bareReference;
    if (getShouldAnnotateModuleName(moduleName)) {
        const originalName = moduleName;
        let matchModuleName = moduleName;
        if (moduleName.endsWith('?')) {
            matchModuleName = moduleName.substring(0, moduleName.length - 1);
        }
        moduleName = resolver.getNextSequentialName(model.designId, matchModuleName);
        m.name = moduleName;
        if (drawProperties && Object.prototype.hasOwnProperty.call(drawProperties, 'Reference')) {
            m.drawProperties['Reference'].value = moduleName;
            m.drawProperties['Reference'].metadata = {
                ...(m.drawProperties['Reference'].metadata ?? {}),
                originalName,
            };
        }
    }
    else if (isAnnotatedKicadModuleName(moduleName)) {
        moduleName = resolver.validateAnnotatedName(model.designId, moduleName);
        m.name = moduleName;
    }
    else {
        moduleName = resolver.validateSourceModuleName(model.designId, moduleName, m.ref);
        m.name = moduleName;
    }
    if (setDrawingValueToValue && value) {
        if (drawProperties && Object.prototype.hasOwnProperty.call(drawProperties, 'Value')) {
            m.drawProperties['Value'].value = value;
        }
    }
    if (m.ref.length === 0) {
        warnings.push(`Module ${m.name} did not have an id`);
    }
    const shouldStoreSourceReference = shouldStoreKicadSourceReference(libraryUnitCount, Boolean(component?.isMultiUnit), m.name, sourceReference);
    if (typeof unitNo === 'number' || shouldStoreSourceReference) {
        m.metadata = {
            ...(typeof unitNo === 'number' ? { [constants_1.META_KICAD_UNIT]: unitNo } : {}),
            ...(shouldStoreSourceReference ? { 'kicad.sourceReference': sourceReference } : {}),
        };
    }
    if (component) {
        let symbol = selectPlacedUnitSymbol(component.symbols, unitNo, bodyStyle, unitStr);
        if (!symbol && unitNo && component.symbols.length >= unitNo) {
            symbol = component.symbols[unitNo - 1];
            if (symbol && symbol.metadata?.unit !== 0 && symbol.metadata?.unit !== unitNo) {
                warnings.push(`Could not find expected symbol with name '${symbolName}', using '${symbol.name}' instead`);
            }
        }
        if (symbol) {
            m.footprintRef = symbol.name;
            const placedPins = hasExplicitUnitSymbols ?
                [
                    ...new Set(component.symbols
                        .filter((s) => (s.metadata?.unit === 0 || s.metadata?.unit === unitNo) &&
                        (s.metadata?.bodyStyle === 0 || s.metadata?.bodyStyle === bodyStyle))
                        .flatMap((s) => s.pins)),
                ]
                : symbol.pins;
            const pinNumbersByName = new Map();
            const pinCountByNumberAndDefaultName = new Map();
            const pinOccurrenceByNumberAndDefaultName = new Map();
            for (const pin of placedPins) {
                if (pin.type === 'pin' && pin.metadata?.[KicadNetNamingStrategy_1.KICAD_PIN_ELECTRICAL_TYPE_KEY] !== 'no_connect') {
                    const numbers = pinNumbersByName.get(pin.name) ?? new Set();
                    numbers.add(pin.number);
                    pinNumbersByName.set(pin.name, numbers);
                    const defaultName = getDefaultPinName(pin);
                    const countsByDefaultName = pinCountByNumberAndDefaultName.get(pin.number) ?? new Map();
                    countsByDefaultName.set(defaultName, (countsByDefaultName.get(defaultName) ?? 0) + 1);
                    pinCountByNumberAndDefaultName.set(pin.number, countsByDefaultName);
                }
            }
            for (const drawing of placedPins) {
                if (drawing.type !== 'pin') {
                    continue;
                }
                const netPosition = (0, Transform_1.translatePoint)(pos, (0, Transform_1.getXyWithMirror)((0, Transform_1.rotatePoint)(drawing.start, -angle), mirrored));
                const connection = {
                    moduleRef: m.ref,
                    pinRef: drawing.ref.toString(),
                    moduleName: m.name,
                };
                if (drawing.isNetLabel) {
                    const labelValue = KicadNetNamingStrategy_1.KicadNetNamingStrategy.escapeNetName(value ?? drawing.name);
                    opts.globalDriverNames?.add(labelValue);
                    connection.appliesNetName = { label: labelValue, labelType: 'power_pin' };
                }
                else {
                    const electricalTypeFromMeta = drawing.metadata?.[KicadNetNamingStrategy_1.KICAD_PIN_ELECTRICAL_TYPE_KEY];
                    const pinInfo = {
                        refDes: bareReference,
                        unitRefDes: pinUnitReference,
                        pinName: drawing.name,
                        pinNumber: drawing.number,
                        isAnnotated: !bareReference.endsWith('?'),
                        symbolUuid: moduleRef,
                        electricalType: typeof electricalTypeFromMeta === 'string' ?
                            electricalTypeFromMeta
                            : undefined,
                        hasMultiplePinsWithSameName: [...(pinNumbersByName.get(drawing.name) ?? [])].some((n) => n !== drawing.number),
                    };
                    const defaultPinName = getDefaultPinName(drawing);
                    const duplicatePinCount = pinCountByNumberAndDefaultName.get(drawing.number)?.get(defaultPinName) ?? 0;
                    const occurrencesByDefaultName = pinOccurrenceByNumberAndDefaultName.get(drawing.number) ?? new Map();
                    const duplicateIndex = occurrencesByDefaultName.get(defaultPinName) ?? 0;
                    occurrencesByDefaultName.set(defaultPinName, duplicateIndex + 1);
                    pinOccurrenceByNumberAndDefaultName.set(drawing.number, occurrencesByDefaultName);
                    const label = opts.isSheetSymbol ? netCalc.namingStrategy.getPinNetName(pinInfo)
                        : duplicatePinCount > 1 ? netCalc.namingStrategy.nameSymbolPinHint(pinInfo, duplicateIndex)
                            : netCalc.namingStrategy.nameSymbolPin(pinInfo);
                    if (label) {
                        connection.appliesNetName = {
                            label,
                            labelType: opts.isSheetSymbol ? 'sheet_pin'
                                : duplicatePinCount === 1 ? 'pin'
                                    : 'pin_hint',
                        };
                    }
                }
                netCalc.addConnection(netPosition, connection);
            }
        }
        else {
            warnings.push(`Component ${useComponentNameOverride} did not have symbol ${symbolName}`);
        }
    }
    else if (!opts.allowMissingComponent) {
        warnings.push(`Could not find component with name ${useComponentNameOverride}`);
    }
    let metaModule = project.modules.find((m2) => m2.name === (m.fullModuleName ?? m.name));
    if (!metaModule) {
        metaModule = {
            name: m.fullModuleName ?? m.name,
            instances: [],
            componentRef: m.componentRef,
            value: moduleValue,
        };
        project.modules.push(metaModule);
    }
    if (!metaModule.name) {
        metaModule.name = m.fullModuleName ?? m.name;
    }
    if (!metaModule.value) {
        metaModule.value = moduleValue;
    }
    const { ['kicad.sourceReference']: _sourceReference, ...moduleMetadata } = metadata;
    if (Object.entries(moduleMetadata).length > 0) {
        metaModule.metadata = {
            ...(metaModule.metadata ?? {}),
            ...moduleMetadata,
        };
    }
    const drawPropertyValue = m.drawProperties['Value'];
    if (drawPropertyValue) {
        drawPropertyValue.value = (0, Util_1.getValueFromMetadata)(drawPropertyValue.value, metaModule);
    }
    if (metaModule.value) {
        metaModule.value = (0, Util_1.getValueFromMetadata)(metaModule.value, metaModule);
    }
    metaModule.instances.push({
        ref: m.ref,
        designId: model.designId,
    });
    if (!inBom) {
        if (metaModule.hideInBom === false) {
            warnings.push(`Module ${m.name} marked as not in BOM, but instance '${m.ref}' is`);
        }
        metaModule.hideInBom = true;
    }
    if (useComponentNameOverride.includes('NetTie')) {
        metaModule.isDnp = true;
        dnp = true;
    }
    if (dnp) {
        if (metaModule.isDnp === false) {
            warnings.push(`Module ${m.name} marked as DNP, but instance '${m.ref}' is not`);
        }
        metaModule.isDnp = true;
    }
    if (value) {
        value = (0, Util_1.getValueFromMetadata)(value, metaModule);
        if (metaModule.value && metaModule.value !== value) {
            if (!component?.isMultiUnit) {
                warnings.push(`Module ${m.name} has different values '${metaModule.value}' and '${value}'`);
                metaModule.value = value;
            }
        }
        else {
            metaModule.value = value;
        }
    }
    moduleNames?.add(m.name);
    return m;
}
function parseNoConnectPosition(node) {
    const at = node.nodes.find((n) => n.name === 'at');
    if (!at) {
        return undefined;
    }
    const x = parseFloat(at.values[0]);
    const y = parseFloat(at.values[1]);
    return Number.isFinite(x) && Number.isFinite(y) ? [x, y] : undefined;
}
function parseJunction(node) {
    let pos = [];
    node.nodes.forEach((n) => {
        switch (n.name) {
            case 'at': {
                pos = [n.values[0], n.values[1]].map((v) => parseFloat(v));
            }
        }
    });
    return {
        type: 'junction',
        position: pos,
    };
}
function parseText(node) {
    let hidden = (0, nodeIsHidden_1.nodeIsHidden)(node);
    let angle = 0;
    let fontSize = 0;
    let thickness = 0;
    let pos = [0, 0];
    let italic = false;
    const justify = [0, 0];
    const [ref, value] = node.values.map((v) => (0, Util_1.removeQuotes)(v));
    node.nodes.forEach((n) => {
        switch (n.name) {
            case 'at': {
                pos = [parseFloat(n.values[0]), parseFloat(n.values[1])];
                if (n.values.length >= 2) {
                    angle = parseFloat(n.values[2]);
                }
                break;
            }
            case 'effects': {
                if ((0, nodeIsHidden_1.nodeIsHidden)(n)) {
                    hidden = true;
                }
                const fontNode = n.nodes.find((n) => n.name === 'font');
                if (fontNode) {
                    const sizeNode = fontNode.nodes.find((n) => n.name === 'size');
                    if (sizeNode) {
                        fontSize = parseFloat(sizeNode.values[0]);
                    }
                    const thicknessNode = fontNode.nodes.find((n) => n.name === 'thickness');
                    if (thicknessNode) {
                        thickness = parseFloat(thicknessNode.values[0]);
                    }
                    italic = fontNode.values.includes('italic');
                }
                const justifyNode = n.nodes.find((n) => n.name === 'justify');
                if (justifyNode) {
                    if (justifyNode.values.includes('left')) {
                        justify[0] = -1;
                    }
                    if (justifyNode.values.includes('right')) {
                        justify[0] = 1;
                    }
                    if (justifyNode.values.includes('top')) {
                        justify[1] = -1;
                    }
                    if (justifyNode.values.includes('bottom')) {
                        justify[1] = 1;
                    }
                }
                break;
            }
        }
    });
    return {
        hidden,
        drawing: (0, parseTextTransforms_1.parseTextTransforms)({
            type: 'text',
            ref,
            value,
            size: fontSize,
            pos: pos,
            thickness,
            angle,
            justify,
            attr: italic ? ['italic'] : [],
        }),
    };
}
function parseCircle(node, isModule) {
    let pos = [0, 0];
    let radius = 0;
    let filled = 0;
    let strokeWidth = 0;
    const flipY = isModule ? -1 : 1;
    node.nodes.forEach((n) => {
        switch (n.name) {
            case 'center': {
                pos = [parseFloat(n.values[0]), flipY * parseFloat(n.values[1])];
                break;
            }
            case 'radius': {
                radius = parseFloat(n.values[0]);
                break;
            }
            case 'fill': {
                const typeNode = n.nodes.find((v) => v.name === 'type');
                if (typeNode) {
                    filled = typeNode.values[0] === 'none' ? 0 : 1;
                }
                break;
            }
            case 'stroke': {
                const widthNode = n.nodes.find((v) => v.name === 'width');
                if (widthNode) {
                    strokeWidth = parseFloat(widthNode.values[0]);
                }
                break;
            }
        }
    });
    return {
        type: 'circle',
        center: pos,
        radius,
        filled: filled === 1,
        width: strokeWidth || DEFAULT_LINE_WIDTH,
    };
}
function extractSymbolInstances(node, warnings, projectName) {
    const projectBlocks = [];
    for (const instanceNode of node.nodes) {
        if (instanceNode.name !== 'project') {
            const warning = `Unknown instance node ${instanceNode.name}`;
            if (!warnings.includes(warning)) {
                warnings.push(warning);
            }
            continue;
        }
        const project = {
            projectName: (0, Util_1.removeQuotes)(instanceNode.values[0]),
            instances: [],
        };
        for (const projectInstance of instanceNode.nodes) {
            if (projectInstance.name !== 'path') {
                continue;
            }
            const path = (0, project_1.joinKicadInstancePath)((0, Util_1.removeQuotes)(projectInstance.values[0]), '');
            const pathSplit = path.substring(1).split('/');
            const instance = {
                projectName: project.projectName,
                path,
                finalNode: pathSplit[pathSplit.length - 1],
            };
            for (const pathNode of projectInstance.nodes) {
                switch (pathNode.name) {
                    case 'reference':
                        instance.reference = (0, Util_1.removeQuotes)(pathNode.values[0]);
                        break;
                    case 'unit':
                        instance.unit = parseInt(pathNode.values[0]);
                        break;
                    case 'page':
                        instance.page = (0, Util_1.removeQuotes)(pathNode.values[0]);
                        break;
                }
            }
            project.instances.push(instance);
        }
        projectBlocks.push(project);
    }
    const matchingProjects = projectName ? projectBlocks.filter((project) => project.projectName === projectName) : [];
    const selectedProjects = matchingProjects.length > 0 ? matchingProjects : projectBlocks;
    return { instances: selectedProjects.flatMap((project) => project.instances) };
}
function extractLegacySymbolInstances(node) {
    const instances = [];
    for (const pathNode of node.nodes) {
        if (pathNode.name !== 'path') {
            continue;
        }
        const instance = {
            path: (0, project_1.joinKicadInstancePath)((0, Util_1.removeQuotes)(pathNode.values[0]), ''),
        };
        for (const valueNode of pathNode.nodes) {
            if (valueNode.name === 'reference') {
                instance.reference = (0, Util_1.removeQuotes)(valueNode.values[0]);
            }
            else if (valueNode.name === 'unit') {
                instance.unit = parseInt(valueNode.values[0]);
            }
        }
        instances.push(instance);
    }
    return instances;
}
function legacySymbolInstanceForPath(instances, instancePath, symbolUuid) {
    if (!instances || !instancePath || !symbolUuid) {
        return undefined;
    }
    const childPath = joinKicadInstancePathWithoutRoot(instancePath, symbolUuid);
    return instances.find((instance) => instance.path === childPath);
}
function joinKicadInstancePathWithoutRoot(instancePath, symbolUuid) {
    return (0, project_1.joinKicadInstancePath)((0, project_1.kicadInstancePathWithoutRoot)(instancePath), symbolUuid);
}
function kicadReferencePlaceholder(reference) {
    const prefix = reference.match(/^[^0-9?]+/)?.[0] ?? reference;
    return `${prefix}?`;
}
function extractLegacySheetInstances(node) {
    const instances = [];
    for (const pathNode of node.nodes) {
        if (pathNode.name !== 'path') {
            continue;
        }
        const pageNode = pathNode.nodes.find((child) => child.name === 'page');
        instances.push({
            path: (0, project_1.joinKicadInstancePath)((0, Util_1.removeQuotes)(pathNode.values[0]), ''),
            page: pageNode?.values[0] !== undefined ? (0, Util_1.removeQuotes)(pageNode.values[0]) : undefined,
        });
    }
    return instances;
}
function sheetPortSymbolPoints(shape, half) {
    switch (shape) {
        case 'input':
            return {
                angle: 180,
                points: [
                    [-half, half],
                    [0, half],
                    [half, 0],
                    [0, -half],
                    [-half, -half],
                    [-half, half],
                ],
            };
        case 'passive':
            return {
                angle: 0,
                points: [
                    [-half, half],
                    [half, half],
                    [half, -half],
                    [-half, -half],
                    [-half, half],
                ],
            };
        case 'tri_state':
        case 'bidirectional':
            return {
                angle: 0,
                points: [
                    [-half, 0],
                    [0, half],
                    [half, 0],
                    [0, -half],
                    [-half, 0],
                ],
            };
        case 'output':
        default:
            return {
                angle: 0,
                points: [
                    [-half, half],
                    [0, half],
                    [half, 0],
                    [0, -half],
                    [-half, -half],
                    [-half, half],
                ],
            };
    }
}
const PORT_LABEL_INSET = 1.349;
const PORT_LABEL_STROKE_BEARING = 0.215;
function parseSheetPins(sheetNode, sheetStart, sheetSize) {
    const halfW = sheetSize[0] / 2;
    const halfH = sheetSize[1] / 2;
    const symbolLayer = (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Symbol).id;
    const nameLayer = (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.PinName).id;
    const pinLayer = (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Pin).id;
    const pins = [];
    let index = 0;
    for (const pinNode of sheetNode.nodes) {
        if (pinNode.name !== 'pin') {
            continue;
        }
        const name = (0, Util_1.removeQuotes)(pinNode.values[0] ?? '');
        const shape = pinNode.values[1] ?? 'passive';
        let at = [0, 0];
        let angle = 0;
        const justify = [0, 0];
        for (const child of pinNode.nodes) {
            if (child.name === 'at') {
                at = [parseFloat(child.values[0]), parseFloat(child.values[1])];
                if (child.values.length > 2) {
                    angle = (0, Transform_1.clampRotation)(parseFloat(child.values[2]));
                }
            }
            else if (child.name === 'effects') {
                const justifyNode = child.nodes.find((n) => n.name === 'justify');
                if (justifyNode) {
                    if (justifyNode.values.includes('left')) {
                        justify[0] = -1;
                    }
                    if (justifyNode.values.includes('right')) {
                        justify[0] = 1;
                    }
                    if (justifyNode.values.includes('top')) {
                        justify[1] = -1;
                    }
                    if (justifyNode.values.includes('bottom')) {
                        justify[1] = 1;
                    }
                }
            }
        }
        const ref = index.toString();
        const connectionPoint = [at[0] - sheetStart[0], at[1] - sheetStart[1]];
        const drawPos = [connectionPoint[0] - halfW, connectionPoint[1] - halfH];
        const inward = angle === 180 ? [1, 0]
            : angle === 90 ? [0, 1]
                : angle === 270 ? [0, -1]
                    : [-1, 0];
        const glyphHalf = 0.5;
        const { points, angle: shapeAngle } = sheetPortSymbolPoints(shape, glyphHalf);
        const glyphAngle = (0, Transform_1.clampRotation)(shapeAngle - angle);
        const glyphOrigin = [drawPos[0] + inward[0] * glyphHalf, drawPos[1] + inward[1] * glyphHalf];
        const portSymbol = {
            type: 'polygon',
            width: DEFAULT_LINE_WIDTH,
            points: points.map((p) => (0, Transform_1.translatePoint)((0, Transform_1.rotatePoint)(p, glyphAngle), glyphOrigin)),
        };
        let justifyX = justify[0];
        if (justifyX === 0) {
            justifyX = angle === 180 ? -1 : 1;
        }
        const isHorizontalEdge = inward[1] === 0;
        const labelInset = isHorizontalEdge && justifyX > 0 ? PORT_LABEL_INSET + PORT_LABEL_STROKE_BEARING : PORT_LABEL_INSET;
        const label = (0, parseTextTransforms_1.parseTextTransforms)({
            type: 'text',
            value: name,
            pos: [drawPos[0] + inward[0] * labelInset, drawPos[1] + inward[1] * labelInset],
            angle: 0,
            justify: [justifyX, justify[1]],
            size: 1,
            thickness: 1,
        });
        pins.push({
            type: 'pin',
            ref,
            name,
            number: ref,
            start: connectionPoint,
            end: connectionPoint,
            layer: pinLayer,
            metadata: { [KicadNetNamingStrategy_1.KICAD_PIN_ELECTRICAL_TYPE_KEY]: shape },
            drawings: [
                { layers: [symbolLayer], drawing: portSymbol },
                { layers: [nameLayer], drawing: label },
            ],
        });
        index++;
    }
    return pins;
}
function parseSheet(def, project, model, sch, node, sheetSymbolIndex, warnings, netCalc, resolver) {
    const strokeWidth = 0;
    let { start, size, sheetname, sheetfile, uuid } = (0, v6ExtractSheetInfo_1.extractSheetInfo)(node);
    if (!sheetfile) {
        warnings.push(`Sheet reference did not have a Sheetfile property`);
        if (sheetname) {
            sheetfile = sheetname;
        }
        else {
            sheetfile = '<unknown>';
        }
    }
    const childInstancePath = sch.instancePath && uuid ? (0, project_1.joinKicadInstancePath)(sch.instancePath, uuid) : undefined;
    const childFilename = simplePathResolve(sheetfile, sch.filename);
    const sourceSymbolKey = uuid ? `${sheetSymbolIndex}:${uuid}` : undefined;
    const occurrenceMatch = def.kicadSchematics.find((sheet) => sheet.parentInstancePath === sch.instancePath &&
        sheet.sourceSymbolKey === sourceSymbolKey &&
        sheet.filename === childFilename);
    const foundSheetDesignId = occurrenceMatch ??
        (childInstancePath ?
            (def.kicadSchematics.find((sheet) => sheet.instancePath === childInstancePath && sheet.filename === childFilename) ?? def.kicadSchematics.find((sheet) => sheet.instancePath === childInstancePath))
            : def.kicadSchematics.find((sheet) => sheet.sourceSheetUuid === uuid || sheet.uuid === uuid));
    if (!foundSheetDesignId) {
        warnings.push(`Did not find input sheet for reference: ${sheetfile}`);
    }
    if (!sheetname) {
        sheetname = sheetfile;
    }
    const compName = `schref_${uuid || sheetfile}`;
    if (!project.components[compName]) {
        const symbols = parseSymbol(node, compName, warnings, netCalc.netRefCtx, undefined, {}, layerSelector, resolver);
        if (symbols.length > 0) {
            if (symbols.length !== 1) {
                warnings.push(`Schematic reference symbol ${compName} yielded ${symbols.length} symbols`);
            }
            symbols[0].pins = parseSheetPins(node, start, size);
            const pinNames = new Set();
            for (const pin of symbols[0].pins) {
                if (pinNames.has(pin.name.valueOf())) {
                    warnings.push(`Schematic reference symbol ${compName} has duplicate pin name ${pin.name}`);
                }
                pinNames.add(pin.name.valueOf());
            }
            const strokeRect = {
                type: 'rect',
                start: [-size[0] / 2, -size[1] / 2],
                end: [size[0] / 2, size[1] / 2],
                strokeWidth: (0, Utils_1.max)(strokeWidth, 0.127),
                filled: false,
            };
            symbols[0].drawings.push({
                layers: [(0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Symbol).id],
                drawing: strokeRect,
            });
            project.components[compName] = {
                name: compName,
                footprints: [],
                numPads: 0,
                numPins: symbols.reduce((p, c) => p + c.pins.length, 0),
                symbols,
                signals: symbols[0].pins.map((p) => ({ id: p.ref, name: p.name, pinRefs: [p.ref], padRefs: [] })),
                isVirtual: true,
            };
        }
        else {
            warnings.push(`Schematic reference symbol ${compName} yielded no symbols`);
        }
    }
    const drawModule = parseModule(project, node, warnings, model, netCalc, resolver, {
        defaultComponentName: compName,
        moduleName: compName,
        hideInBom: true,
        isDnp: true,
        isSheetSymbol: true,
        instancePath: sch.instancePath,
        annotationPath: sch.annotationPath,
        projectName: sch.projectName,
        legacySymbolInstances: sch.legacySymbolInstances,
        sourceInstanceCount: sch.sourceInstanceCount,
    });
    model.modules.push(drawModule);
    drawModule.center = [start[0] + size[0] / 2, start[1] + size[1] / 2];
    const schRef = {
        type: 'schref',
        file: sheetfile,
        name: sheetname,
        designId: foundSheetDesignId?.uuid ?? uuid ?? null,
        drawModule: drawModule,
    };
    return schRef;
}
exports.KICAD_PIN_ELECTRICAL_TYPES = [
    'input',
    'output',
    'bidirectional',
    'tri_state',
    'passive',
    'free',
    'unspecified',
    'power_in',
    'power_out',
    'open_collector',
    'open_emitter',
    'no_connect',
];
function parsePin(symbolNode, node, isModule, defaultNumber, netRefCtx, metadata, rootOffset) {
    let name = '';
    let start = [0, 0];
    let rotation = 0;
    let length = 0;
    let showName = true;
    let showNumber = true;
    let nameOffset;
    let numberOffset;
    let electricalType;
    const nameText = {
        type: 'text',
        pos: [0, 0],
        attr: [],
        justify: [-1, 0],
        size: 1,
        thickness: 1,
        value: '',
    };
    const numberText = {
        type: 'text',
        pos: [0, 0],
        attr: [],
        justify: [0, -1],
        size: 1,
        thickness: 1,
        value: defaultNumber,
    };
    const flipY = isModule ? -1 : 1;
    for (const v of node.values) {
        if (exports.KICAD_PIN_ELECTRICAL_TYPES.includes(v)) {
            electricalType = v;
            break;
        }
    }
    for (const v of node.values) {
        switch (v) {
            case 'hide': {
                showName = false;
                showNumber = false;
                break;
            }
            default: {
                if (v.startsWith('"') && v.endsWith('"')) {
                    name = (0, Util_1.removeQuotes)(v);
                }
            }
        }
    }
    node.nodes.forEach((n) => {
        switch (n.name) {
            case 'at': {
                start = [parseFloat(n.values[0]), flipY * parseFloat(n.values[1])];
                if (n.values.length === 3) {
                    rotation = flipY * parseFloat(n.values[2]);
                }
                break;
            }
            case 'length': {
                length = parseFloat(n.values[0]);
                break;
            }
            case 'hide': {
                if ((0, nodeIsHidden_1.nodeIsHidden)(node)) {
                    showName = false;
                    showNumber = false;
                }
                break;
            }
            case 'name': {
                name = (0, Util_1.removeQuotes)(n.values[0]);
                nameText.value = name;
                const fontSizeNode = n.nodes
                    .find((n) => n.name === 'effects')
                    ?.nodes.find((n) => n.name === 'font')
                    ?.nodes?.find((n) => n.name === 'size');
                if (fontSizeNode) {
                    nameText.size = parseFloat(fontSizeNode.values[0]);
                    nameText.thickness = parseFloat(fontSizeNode.values[0]);
                }
                if ((0, nodeIsHidden_1.nodeIsHidden)(n) || nameText.value === '~') {
                    showName = false;
                }
                break;
            }
            case 'number': {
                numberText.value = (0, Util_1.removeQuotes)(n.values[0]);
                const fontSizeNode = n.nodes
                    .find((n) => n.name === 'effects')
                    ?.nodes.find((n) => n.name === 'font')
                    ?.nodes?.find((n) => n.name === 'size');
                if (fontSizeNode) {
                    numberText.size = parseFloat(fontSizeNode.values[0]);
                    numberText.thickness = parseFloat(fontSizeNode.values[0]);
                }
                if ((0, nodeIsHidden_1.nodeIsHidden)(n)) {
                    showNumber = false;
                }
                break;
            }
        }
    });
    symbolNode.nodes.forEach((n) => {
        switch (n.name) {
            case 'pin_numbers': {
                const offsetNode = n.nodes.find((n) => n.name === 'offset');
                if (offsetNode) {
                    numberOffset = parseFloat(offsetNode.values[0]);
                }
                if ((0, nodeIsHidden_1.nodeIsHidden)(n)) {
                    showNumber = false;
                }
                break;
            }
            case 'pin_names': {
                const offsetNode = n.nodes.find((n) => n.name === 'offset');
                if (offsetNode) {
                    nameOffset = parseFloat(offsetNode.values[0]);
                }
                if ((0, nodeIsHidden_1.nodeIsHidden)(n)) {
                    showName = false;
                }
                break;
            }
        }
    });
    const end = (0, Transform_1.getLineOffset)(length, rotation, start);
    nameText.pos = (0, Transform_1.translatePoint)(end, (0, Transform_1.getLineOffset)(typeof nameOffset === 'undefined' ? nameText.size / 4 : nameOffset, rotation, [0, 0]));
    numberText.pos = (0, Transform_1.translatePoint)(end, (0, Transform_1.getLineOffset)(typeof numberOffset === 'undefined' ? -length / 2 : -numberOffset, rotation, [0, 0]));
    numberText.angle = nameText.angle = -rotation;
    numberText.mirror = nameText.mirror = (0, getMirrorValuesToKeepTextRightSideUp_1.getMirrorValuesToKeepTextRightSideUp)(rotation);
    if (nameText.mirror[0]) {
        nameText.justify = [1, 0];
        numberText.justify = [0, -1];
    }
    const pin = {
        type: 'pin',
        ref: numberText.value || name,
        name,
        number: numberText.value,
        start: (0, Transform_1.translatePoint)(start, rootOffset),
        end: (0, Transform_1.translatePoint)(end, rootOffset),
        layer: (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Pin).id,
        drawings: [],
        ...(electricalType ? { metadata: { [KicadNetNamingStrategy_1.KICAD_PIN_ELECTRICAL_TYPE_KEY]: electricalType } } : {}),
    };
    const isNetLabel = node.values.includes('power_in') &&
        symbolNode.nodes.some((n) => n.name === 'power') &&
        metadata?.Reference === '#PWR';
    if (isNetLabel) {
        pin.isNetLabel = isNetLabel;
        pin.name = sigma_engine_shared_1.NetRef.fromString(netRefCtx, metadata?.Value?.toString() ?? pin.name).strCast();
    }
    if (showName && nameText.value.length > 0) {
        pin.drawings.push({
            layers: [(0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.PinName).id],
            drawing: (0, parseTextTransforms_1.parseTextTransforms)(nameText),
        });
    }
    if (showNumber && numberText.value.length > 0) {
        pin.drawings.push({
            layers: [(0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.PinNumber).id],
            drawing: numberText,
        });
    }
    if (pin.drawings.length === 0) {
        delete pin.drawings;
    }
    return pin;
}
function parseWire(node) {
    let start = [0, 0];
    let end = [0, 0];
    let stroke;
    node.nodes.forEach((n) => {
        switch (n.name) {
            case 'pts': {
                const pts = n.nodes.map((pt) => [pt.values[0], pt.values[1]].map((v) => parseFloat(v)));
                start = pts[0];
                end = pts[1];
                break;
            }
            case 'stroke':
                stroke = (0, v6Common_1.parseStrokeDirective)(n);
                break;
        }
    });
    return {
        type: 'track',
        start,
        end,
        width: stroke?.width || 0,
    };
}
function parseBusEntry(node) {
    let start = [0, 0];
    let size = [0, 0];
    let stroke;
    let rotation = 0;
    node.nodes.forEach((n) => {
        switch (n.name) {
            case 'at': {
                start = [parseFloat(n.values[0]), parseFloat(n.values[1])];
                if (n.values.length === 3) {
                    rotation += parseFloat(n.values[2]);
                }
                break;
            }
            case 'size': {
                size = [parseFloat(n.values[0]), parseFloat(n.values[1])];
                break;
            }
            case 'stroke': {
                stroke = (0, v6Common_1.parseStrokeDirective)(n);
                break;
            }
        }
    });
    return (0, Transform_1.rotateDrawing)({
        type: 'track',
        start,
        end: (0, Transform_1.translatePoint)(size, start),
        width: stroke?.width || 0,
        metadata: {
            'kicad.busentry': true,
        },
    }, rotation);
}
function parsePolyline(node, isModule) {
    const { points, strokeWidth, style, fillType } = (0, v6ExtractPolygon_1.extractPolygon)(isModule, node);
    const lastPoint = points[points.length - 1];
    if (points[0][0] !== lastPoint[0] || points[0][1] !== lastPoint[1]) {
        const segmentFlipY = isModule ? -1 : 1;
        const segments = [];
        for (let i = 0; i < points.length - 1; ++i) {
            segments.push({
                type: 'segment',
                start: [points[i][0], segmentFlipY * points[i][1]],
                end: [points[i + 1][0], segmentFlipY * points[i + 1][1]],
                width: strokeWidth || DEFAULT_LINE_WIDTH,
                style,
            });
        }
        return segments;
    }
    return [
        {
            type: 'polygon',
            filled: fillType !== 'none',
            width: strokeWidth || DEFAULT_LINE_WIDTH,
            points,
            lineStyle: style,
        },
    ];
}
function parseHierarchicalLabel(project, node, netCalc, warnings, resolver) {
    const value = (0, Util_1.removeQuotes)(node.values[0]);
    const { pos, direction, justify, fontSize, shape } = (0, v6ParseLabel_1.extractLabelInfo)(node, false, warnings);
    let portDirection;
    const symbolWidth = 1;
    const halfSymWidth = symbolWidth / 2;
    const symbolDrawing = {
        type: 'polygon',
        width: 0,
        points: [],
        pos: [halfSymWidth, 0],
    };
    switch (shape) {
        case 'input':
            symbolDrawing.angle = 180;
            portDirection = sigma_engine_shared_2.DesignModels.SignalDirection.Input;
            symbolDrawing.points = [
                [-halfSymWidth, halfSymWidth],
                [0, halfSymWidth],
                [halfSymWidth, 0],
                [0, -halfSymWidth],
                [-halfSymWidth, -halfSymWidth],
                [-halfSymWidth, halfSymWidth],
            ];
            break;
        case 'output':
            portDirection = sigma_engine_shared_2.DesignModels.SignalDirection.Output;
            symbolDrawing.points = [
                [-halfSymWidth, halfSymWidth],
                [0, halfSymWidth],
                [halfSymWidth, 0],
                [0, -halfSymWidth],
                [-halfSymWidth, -halfSymWidth],
                [-halfSymWidth, halfSymWidth],
            ];
            break;
        case 'passive':
            symbolDrawing.points = [
                [-halfSymWidth, halfSymWidth],
                [halfSymWidth, halfSymWidth],
                [halfSymWidth, -halfSymWidth],
                [-halfSymWidth, -halfSymWidth],
                [-halfSymWidth, halfSymWidth],
            ];
            break;
        case 'tri_state':
        case 'bidirectional':
            portDirection = sigma_engine_shared_2.DesignModels.SignalDirection.BiDirectionalIO;
            symbolDrawing.points = [
                [-halfSymWidth, 0],
                [0, halfSymWidth],
                [halfSymWidth, 0],
                [0, -halfSymWidth],
                [-halfSymWidth, 0],
            ];
            break;
        default:
            warnings.push(`Unsupported hierarchical label shape: ${shape}`);
            break;
    }
    const validatedNetName = resolver.validateSourceNetName(project.designId, value, 'hierarchical_label');
    const valueRef = netCalc.setLabel(pos, validatedNetName, 'hier_label');
    const textDrawing = (0, parseTextTransforms_1.parseTextTransforms)({
        type: 'text',
        value,
        pos: [symbolWidth * 1.25, 0],
        angle: 0,
        justify: [justify[0], 0],
        size: fontSize,
        thickness: 1,
        mirror: (0, getMirrorValuesToKeepTextRightSideUp_1.getMirrorValuesToKeepTextRightSideUp)(direction),
    });
    project.ports[value] = {
        name: value,
        nets: [{ name: valueRef.strCast(), label: value }],
        direction: portDirection,
    };
    return {
        type: 'group',
        pos,
        angle: direction,
        net: valueRef.strCast(),
        drawings: [symbolDrawing, textDrawing],
    };
}
function parseGlobalLabel(node, netRefCtx, warnings) {
    let [value] = node.values;
    value = (0, Util_1.removeQuotes)(value);
    const SHAPES = ['input', 'output', 'bidirectional', 'neutral'];
    let shape = 'neutral';
    let x = -1;
    let y = -1;
    let fontSize = -1;
    let thickness = -1;
    let direction = 0;
    node.nodes.forEach((n) => {
        switch (n.name) {
            case 'shape': {
                const shapeValue = n.values[0];
                if (!SHAPES.includes(shapeValue)) {
                    warnings.push(`Label '${value}' had unexpected shape value: '${shapeValue}'`);
                }
                shape = shapeValue;
                break;
            }
            case 'at': {
                x = parseFloat(n.values[0]);
                y = parseFloat(n.values[1]);
                if (n.values.length > 2) {
                    direction = parseFloat(n.values[2]);
                }
                break;
            }
            case 'effects': {
                const fontNode = n.nodes.filter((n) => n.name === 'font');
                if (fontNode?.length) {
                    const sizeNode = fontNode[0].nodes.filter((n) => n.name === 'size');
                    if (sizeNode?.length) {
                        fontSize = parseFloat(sizeNode[0].values[0]);
                    }
                    const thicknessNode = fontNode[0].nodes.filter((n) => n.name === 'thickness');
                    if (thicknessNode?.length) {
                        thickness = parseFloat(thicknessNode[0].values[0]);
                    }
                }
                break;
            }
            case 'property': {
                break;
            }
        }
    });
    let orientation = 'L';
    direction = Math.abs(direction);
    if (direction <= 45) {
        orientation = 'L';
    }
    else if (direction <= 135) {
        orientation = 'D';
    }
    else if (direction <= 225) {
        orientation = 'R';
    }
    else {
        orientation = 'U';
    }
    return {
        type: 'label',
        pos: [x, y],
        value,
        shape,
        orientation,
        metadata: {
            fontSize,
            thickness,
        },
        net: sigma_engine_shared_1.NetRef.fromString(netRefCtx, value).strCast(),
    };
}
function parseComponents(project, node, netRefCtx, resolver) {
    node.nodes.forEach((n) => {
        switch (n.name) {
            case 'symbol': {
                const componentDefinition = parseComponentDefinition(n, netRefCtx, project.warnings, layerSelector, resolver);
                if (!componentDefinition) {
                    break;
                }
                if (!project.components[componentDefinition.name]) {
                    project.components[componentDefinition.name] = componentDefinition;
                }
                break;
            }
        }
    });
}
function parseComponentDefinition(node, netRefCtx, warnings, layerSelector, resolver) {
    const name = (0, Util_1.removeQuotes)(node.values[0]);
    const metadata = { ...kicadSymbolParser_1.KicadSymbolNameParser.parseSymbolName(name) };
    let symbols = [];
    let signals = [];
    node.nodes.forEach((n) => {
        switch (n.name) {
            case 'property': {
                const name = (0, Util_1.removeQuotes)(n.values[0]);
                const value = (0, Util_1.cleanMetadataVal)((0, Util_1.removeQuotes)(n.values[1]));
                if (value && value.length > 0 && value !== '~') {
                    metadata[name] = value;
                }
                break;
            }
            case 'symbol': {
                const parsedSymbols = parseSymbol(n, name, warnings, netRefCtx, node, metadata, layerSelector, resolver);
                for (const symbol of parsedSymbols) {
                    const pinRefsMap = new Map();
                    const pinNameMap = new Map();
                    for (const pin of symbol.pins) {
                        const name = pin.name.valueOf();
                        if (!pinRefsMap.has(pin.ref)) {
                            pinRefsMap.set(pin.ref, [pin]);
                        }
                        else {
                            pinRefsMap.get(pin.ref).push(pin);
                        }
                        if (!pinNameMap.has(name)) {
                            pinNameMap.set(name, new Map([
                                [
                                    {
                                        start: pin.start,
                                        end: pin.end,
                                    },
                                    [pin],
                                ],
                            ]));
                        }
                        else {
                            const pinMap = pinNameMap.get(name);
                            const existingPin = Array.from(pinMap.keys()).find((p) => {
                                return (Math.abs(p.start[0] - pin.start[0]) < 0.0001 &&
                                    Math.abs(p.start[1] - pin.start[1]) < 0.0001 &&
                                    Math.abs(p.end[0] - pin.end[0]) < 0.0001 &&
                                    Math.abs(p.end[1] - pin.end[1]) < 0.0001);
                            });
                            if (existingPin) {
                                pinMap.get(existingPin).push(pin);
                            }
                            else {
                                pinMap.set({
                                    start: pin.start,
                                    end: pin.end,
                                }, [pin]);
                            }
                        }
                    }
                    for (const [pinName, pinPositionMaps] of pinNameMap) {
                        if (pinPositionMaps.size === 1) {
                            const [pins] = Array.from(pinPositionMaps.values());
                            const pin = pins[0];
                            if (signals.some((s) => s.pinRefs.includes(pin.ref))) {
                                continue;
                            }
                            signals.push({
                                id: pinName,
                                name: pin.name,
                                pinRefs: pins.map((p) => p.ref),
                                padRefs: pins.map((p) => p.number),
                                metadata: {
                                    fromUnit: symbol.metadata?.unit,
                                },
                            });
                        }
                        else {
                            for (const pin of Array.from(pinPositionMaps.values()).flat()) {
                                if (signals.some((s) => s.pinRefs.includes(pin.ref))) {
                                    continue;
                                }
                                const pinRefs = pinRefsMap.get(pin.ref);
                                signals.push({
                                    id: pinRefs.length > 1 ? pin.number : pin.ref,
                                    name: pin.name.valueOf(),
                                    pinRefs: [pin.ref],
                                    padRefs: [pin.number],
                                    metadata: {
                                        fromUnit: symbol.metadata?.unit,
                                    },
                                });
                            }
                        }
                    }
                }
                symbols = symbols?.concat(parsedSymbols);
                n.nodes.forEach((n2) => {
                    switch (n2.name) {
                        case 'property': {
                            if (n2.values.length === 2) {
                                const val = (0, Util_1.removeQuotes)(n2.values[1]);
                                if (val && val !== '~') {
                                    metadata[(0, Util_1.removeQuotes)(n2.values[0]).toLowerCase()] = (0, Util_1.cleanMetadataVal)(val);
                                }
                            }
                        }
                    }
                });
                break;
            }
        }
    });
    const lowestBodyStyleByUnit = new Map();
    for (const symbol of symbols) {
        if (symbol.pins.length === 0) {
            continue;
        }
        const current = lowestBodyStyleByUnit.get(symbol.metadata?.unit);
        if (!current || (symbol.metadata?.bodyStyle ?? 0) < (current.metadata?.bodyStyle ?? 0)) {
            lowestBodyStyleByUnit.set(symbol.metadata?.unit, symbol);
        }
    }
    const isMultiUnit = lowestBodyStyleByUnit.size > 1;
    const nonzeroBodyStyles = new Set(symbols.map((symbol) => symbol.metadata?.bodyStyle).filter((bodyStyle) => bodyStyle !== undefined && bodyStyle > 0));
    const bodyStyles = nonzeroBodyStyles.size > 0 ? [...nonzeroBodyStyles] : [0];
    const hasExplicitBodyStyles = symbols.some((symbol) => typeof symbol.metadata?.bodyStyle === 'number');
    const numPins = hasExplicitBodyStyles ?
        Math.max(0, ...bodyStyles.map((bodyStyle) => new Set(symbols
            .filter((symbol) => symbol.metadata?.bodyStyle === 0 || symbol.metadata?.bodyStyle === bodyStyle)
            .flatMap((symbol) => symbol.pins)).size))
        : [...lowestBodyStyleByUnit.values()].reduce((total, symbol) => total + symbol.pins.length, 0);
    symbols = symbols.map((symbol) => {
        if (!symbol.metadata) {
            throw new Error(`Symbol ${symbol.name} has no metadata`);
        }
        if (symbol.metadata?.unit === 0 && symbol.metadata?.bodyStyle === 0) {
            return symbol;
        }
        const mergeWith = symbols.filter((s) => s !== symbol &&
            (s.metadata?.bodyStyle === 0 || s.metadata?.bodyStyle === symbol.metadata?.bodyStyle) &&
            (s.metadata?.unit === 0 || s.metadata?.unit === symbol.metadata?.unit) &&
            s.metadata?.orientation === symbol.metadata?.orientation);
        return mergeWith.reduce((acc, curr) => mergeSymbols(curr, acc), symbol);
    });
    if (!symbols || symbols.length === 0) {
        warnings.push(`Could not load component symbol ${name}. Creating empty symbol.`);
        symbols.push({
            ref: name,
            name,
            drawings: [],
            pins: [],
            metadata: { ...kicadSymbolParser_1.KicadSymbolNameParser.parseSymbolName(name) },
        });
    }
    if (signals.some((s) => signals.some((s2) => s.id === s2.id && s !== s2))) {
        const remapSignalIds = new Set();
        for (const signal of signals) {
            if (remapSignalIds.has(signal.id) || signals.some((s) => s.id === signal.id && s !== signal)) {
                remapSignalIds.add(signal.id);
                signal.id = `${signal.metadata?.fromUnit ?? signals.indexOf(signal)}${signal.id}`;
            }
        }
    }
    const isSupplySymbol = symbols.some((s) => s.pins.some((p) => p.isNetLabel === true));
    const reference = metadata.Reference;
    const hasVirtualReference = typeof reference === 'string' && reference.startsWith('#');
    let component = {
        name,
        numPins: numPins,
        numPads: 0,
        footprints: [],
        symbols,
        signals,
        ...(isSupplySymbol || hasVirtualReference ? { isVirtual: true } : {}),
    };
    if (name.includes('NetTie')) {
        component.model = { modelId: 'jumper.2h' };
    }
    if (isMultiUnit) {
        component.isMultiUnit = true;
        for (const sym of symbols) {
            const unitNoStr = /_([0-9]+)_([0-9]+)$/.exec(sym.name)?.[1];
            if (!unitNoStr) {
                continue;
            }
            const unitNo = unitNoStr ? parseInt(unitNoStr) : 1;
            const blockPattern = String.fromCharCode('A'.charCodeAt(0) + unitNo - 1);
            for (const pin of sym.pins) {
                const pinSignal = component.signals.find((s) => s.pinRefs.includes(pin.ref));
                pinSignal.blockInfo = {
                    index: unitNo,
                    blockPattern,
                };
            }
        }
    }
    if (Object.keys(metadata).length > 0) {
        component.metadata = metadata;
    }
    if (resolver?.updateAltiumSupplySymbol) {
        component = resolver.updateAltiumSupplySymbol(component);
    }
    if (component.isVirtual) {
        for (const signal of component.signals) {
            signal.padRefs = [];
        }
    }
    return component;
}
function parseSymbol(node, name, warnings, netRefCtx, parentSymbolNode = node, metadata, layerSelector, resolver) {
    const symbols = [];
    name = node.values[0] ? (0, Util_1.removeQuotes)(node.values[0]) : name;
    if (resolver?.removeAltiumSuffixFromSymbol) {
        name = resolver.removeAltiumSuffixFromSymbol(name, metadata);
    }
    const basePosition = [0, 0];
    const symbol = {
        ref: name,
        name,
        drawings: [],
        pins: [],
        metadata: { ...kicadSymbolParser_1.KicadSymbolNameParser.parseSymbolName(name) },
    };
    let subSymbolNum = 0;
    node.nodes.forEach((n2) => {
        switch (n2.name) {
            case 'at': {
                basePosition[0] = -parseFloat(n2.values[0]);
                basePosition[1] = parseFloat(n2.values[1]);
                break;
            }
        }
    });
    node.nodes.forEach((n2) => {
        switch (n2.name) {
            case 'rectangle': {
                symbol.drawings.push(...(0, v6ParseRectangle_1.parseRectangle)(n2, true, warnings, DesignModels_1.SchLayerType.Symbol, layerSelector, Constants_1.SEGMENT_MIN_WIDTH));
                break;
            }
            case 'pin': {
                symbol.pins.push(parsePin(parentSymbolNode, n2, true, symbol.pins.length.toString(), netRefCtx, metadata, basePosition));
                break;
            }
            case 'polyline': {
                symbol.drawings.push(...parsePolyline(n2, true).map((drawing) => ({
                    layers: [(0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Symbol).id],
                    drawing,
                })));
                break;
            }
            case 'circle': {
                symbol.drawings.push({
                    layers: [(0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Symbol).id],
                    drawing: parseCircle(n2, true),
                });
                break;
            }
            case 'arc': {
                const arcDrawing = (0, v6ParseArc_1.parseArc)(true, n2, false, warnings, (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Symbol).name, layerSelector, Constants_1.SEGMENT_MIN_WIDTH);
                if (!arcDrawing?.drawing) {
                    warnings.push(`Symbol ${name} has undefined arc drawing`);
                }
                if (arcDrawing?.drawing) {
                    symbol.drawings.push({
                        layers: [(0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Symbol).id],
                        drawing: arcDrawing.drawing,
                    });
                }
                break;
            }
            case 'text': {
                symbol.drawings.push((0, v6ParseLabel_1.parseTextElement)(n2, true, warnings, (0, layers_1.getKicadSchLayerByType)(DesignModels_1.SchLayerType.Label).name, layerSelector));
                break;
            }
            case 'symbol': {
                let symName;
                ++subSymbolNum;
                if (n2.values.length >= 1) {
                    symName = (0, Util_1.removeQuotes)(n2.values[0]);
                }
                else {
                    symName = `${name}_${subSymbolNum}_1`;
                    warnings.push(`Expected symbol name ${symName}`);
                }
                symbols.push(...parseSymbol(n2, symName, warnings, netRefCtx, parentSymbolNode, metadata, layerSelector, resolver));
                break;
            }
            default: {
                warnings.push(`Symbol ${name} had unsupported drawing type ${n2.name}`);
                break;
            }
        }
    });
    if (symbols.length > 0 && symbol.drawings.length > 0) {
        warnings.push(`Symbol ${name} had drawings and sub-symbols`);
    }
    if (symbols.length === 0) {
        symbols.push(symbol);
    }
    return symbols.map((s) => {
        s.pins = ensureUniquePinRefs(s.pins);
        return s;
    });
}
function ensureUniquePinRefs(pins) {
    const occurrencesByRef = new Map();
    const assignedRefs = new Set();
    const realPinNumbers = new Set(pins.map((pin) => pin.number));
    for (const pin of pins) {
        const occurrence = (occurrencesByRef.get(pin.ref) ?? 0) + 1;
        occurrencesByRef.set(pin.ref, occurrence);
        if (occurrence > 1) {
            let suffix = occurrence;
            let candidate = `${pin.ref}#${suffix}`;
            while (realPinNumbers.has(candidate) || assignedRefs.has(candidate)) {
                suffix++;
                candidate = `${pin.ref}#${suffix}`;
            }
            pin.ref = candidate;
        }
        assignedRefs.add(pin.ref);
    }
    return pins;
}
function parseSchematicPaper(model, node) {
    let bbox;
    const isPortrait = Boolean(node.values.find((n) => n.toLowerCase() === 'portrait'));
    switch ((0, Util_1.removeQuotes)(node.values[0]?.toLowerCase())) {
        case 'user': {
            if (node.values.length >= 3) {
                bbox = bboxFromLengthAndWidth(parseFloat(node.values[1]), parseFloat(node.values[2]));
            }
            break;
        }
        case 'a5':
            bbox = bboxFromLengthAndWidth(148, 210);
            break;
        case 'a4':
            bbox = bboxFromLengthAndWidth(210, 297);
            break;
        case 'a3':
            bbox = bboxFromLengthAndWidth(297, 420);
            break;
        case 'a2':
            bbox = bboxFromLengthAndWidth(420, 594);
            break;
        case 'a1':
            bbox = bboxFromLengthAndWidth(594, 841);
            break;
        case 'a0':
            bbox = bboxFromLengthAndWidth(841, 1189);
            break;
        case 'a':
        case 'usletter':
            bbox = bboxFromLengthAndWidth((0, Util_1.convertGridDistanceUnits)(8.5, 'inch', 'mm'), (0, Util_1.convertGridDistanceUnits)(11, 'inch', 'mm'));
            break;
        case 'b':
        case 'usledger':
            bbox = bboxFromLengthAndWidth((0, Util_1.convertGridDistanceUnits)(11, 'inch', 'mm'), (0, Util_1.convertGridDistanceUnits)(17, 'inch', 'mm'));
            break;
        case 'c':
            bbox = bboxFromLengthAndWidth((0, Util_1.convertGridDistanceUnits)(17, 'inch', 'mm'), (0, Util_1.convertGridDistanceUnits)(22, 'inch', 'mm'));
            break;
        case 'd':
            bbox = bboxFromLengthAndWidth((0, Util_1.convertGridDistanceUnits)(22, 'inch', 'mm'), (0, Util_1.convertGridDistanceUnits)(34, 'inch', 'mm'));
            break;
        case 'e':
            bbox = bboxFromLengthAndWidth((0, Util_1.convertGridDistanceUnits)(34, 'inch', 'mm'), (0, Util_1.convertGridDistanceUnits)(44, 'inch', 'mm'));
            break;
        case 'uslegal':
            bbox = bboxFromLengthAndWidth((0, Util_1.convertGridDistanceUnits)(8.5, 'inch', 'mm'), (0, Util_1.convertGridDistanceUnits)(14, 'inch', 'mm'));
            break;
    }
    if (bbox) {
        if (isPortrait) {
            model.edges_bbox = {
                ...bbox,
                maxx: bbox.maxy,
                maxy: bbox.maxx,
            };
        }
        else {
            model.edges_bbox = bbox;
        }
    }
}
function bboxFromLengthAndWidth(length, width) {
    return {
        minx: 0,
        miny: 0,
        maxx: length,
        maxy: width,
    };
}
function mergeSymbols(a, b) {
    const c = {
        ...b,
        drawings: b.drawings.concat(a.drawings),
        pins: b.pins.concat(a.pins),
        metadata: {
            ...(a.metadata ?? {}),
            ...(b.metadata ?? {}),
        },
    };
    return c;
}
function combineNets(ref, first, second) {
    return {
        ...first,
        ...second,
        name: ref.strCast(),
        metadata: {
            ...(first.metadata ?? {}),
            ...(second.metadata ?? {}),
        },
        connections: first.connections.concat(second.connections),
    };
}
//# sourceMappingURL=v6schema.js.map